<template>
    <vx-modal :visible="visible" :padding="false" size="md" @update:visible="emit('update:visible', $event)">
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">Re-schedule Blast</div>
        </template>

        <div class="px-12 pb-12 pt-10 flex flex-col gap-8">
            <vx-alert color="info" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" />
                </template>
                You are re-scheduling a Blast for <strong>{{ props.campaign.sender.name }}</strong>
            </vx-alert>

            <div v-if="loading" class="flex items-center justify-center py-12">
                <vx-icon-loading class="animate-loading-spin text-slate-500 text-2xl" />
            </div>

            <template v-else>
                <div v-if="initialCalendarLoading" class="flex justify-center items-center py-12">
                    <vx-icon-loading class="animate-loading-spin text-2xl text-slate-500" />
                </div>

                <template v-else>
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <span
                                class="text-base font-extrabold text-slate-800 pr-3 mr-4 border-y-0 border-l-0 border-r border-solid border-r-slate-400"
                            >
                                When
                            </span>
                            <span class="text-sm font-semibold text-slate-700 mr-1">
                                {{
                                    canScheduleNow && currentTab === 'now'
                                        ? 'at campaign launch'
                                        : dateWithTzFormatted || '-'
                                }}
                            </span>
                        </div>
                        <vx-tabs v-if="canScheduleNow" v-model="currentTab" size="sm">
                            <vx-tab value="now">Now</vx-tab>
                            <vx-tab value="later">Later</vx-tab>
                        </vx-tabs>
                    </div>

                    <template v-if="canScheduleNow && currentTab === 'now'">
                        <div class="text-center flex justify-center text-slate-600 text-lg font-normal my-12">
                            <span class="text-center block">
                                Your message will send immediately after clicking the update button.
                            </span>
                        </div>
                    </template>

                    <quick-blast-rules-datepicker
                        v-if="!canScheduleNow || currentTab === 'later'"
                        :modelValue="dateAsDate"
                        @update:modelValue="date = $event"
                        :campaignType="campaignType ?? undefined"
                        isRequired
                    />
                </template>

                <vx-alert v-if="!initialCalendarLoading" color="info">
                    You should respect safe sending hours. Texting is allowed between 8 am to 9 pm in the contact's
                    time-zone. We recommend scheduling between 10 am to 6 pm to drive the most engagement.
                </vx-alert>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button @click="emit('update:visible', false)" color="muted" size="lg" data-test="cancel-btn">
                    Cancel
                </vx-button>
                <vx-button
                    @click="reschedule"
                    color="primary"
                    :loading="saving"
                    :disabled="loading || initialCalendarLoading || (currentTab === 'later' && !date)"
                    size="lg"
                    class="grow"
                    data-test="reschedule-btn"
                >
                    Update
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { VxModal, VxButton, VxAlert, VxTabs, VxTab, VxIconLoading } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDiamondExclamation } from '@fortawesome/pro-solid-svg-icons';
import QuickBlastRulesDatepicker from '~/components/quick-blast/QuickBlastRulesDatepicker.vue';
import { useQuickBlastWhen } from '~/components/quick-blast/useQuickBlastWhen';
import { useSendingCalendar } from '~/components/quick-blast/useSendingCalendar';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { campaignsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { getClientTz } from '~/utils/date';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { ref, computed, watch, toRef } from 'vue';
import type { BlastForecastCampaignsResponse } from '~/services/blastForecast/blastForecastClient.types';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    visible: boolean;
    campaign: BlastForecastCampaignsResponse['items'][0];
    orgUnitId?: number;
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'rescheduled', liveAt: string): void;
}>();

const toasted = useToasted();
const { isDateTimeAllowed } = useSendingCalendar();

const loading = ref(false);
const saving = ref(false);
const campaignType = ref<'marketing' | 'transactional' | null>(null);
const currentTab = ref<'now' | 'later'>('later');
const dateModel = ref<Date | string | undefined>();

const {
    date,
    canScheduleNow,
    initialCalendarLoading,
    dateWithTzFormatted,
    campaignTypeRestricted,
    loadCurrentMonthCalendar,
    roundDateToNearestFiveMinutes,
} = useQuickBlastWhen({
    campaignType: toRef(() => campaignType.value ?? undefined),
    modelValue: dateModel,
    currentTab,
});

const dateAsDate = computed((): Date | undefined => {
    if (date.value instanceof Date) {
        return date.value;
    }
    return undefined;
});

const fetchCampaignDetails = async () => {
    if (props.campaign.sender.type === 'org_unit') {
        return campaignsClient.orgUnitCampaignGet(teamId, props.campaign.sender.id, props.campaign.id);
    } else if (props.campaign.sender.type === 'group') {
        return campaignsClient.groupCampaignGet(teamId, props.campaign.sender.id, props.campaign.id);
    }
};

watch(
    () => props.visible,
    async (visible) => {
        if (!visible) {
            return;
        }

        currentTab.value = 'later';
        loading.value = true;
        try {
            const response = await fetchCampaignDetails();

            if (!response) {
                return;
            }

            campaignType.value = response.data.type;

            const existingDate = dayjs.utc(props.campaign.live_at).toDate();
            dateModel.value = existingDate;
            date.value = existingDate;

            if (campaignTypeRestricted.value) {
                roundDateToNearestFiveMinutes(props.campaign.live_at);
                await loadCurrentMonthCalendar();
            }
        } catch (error) {
            toasted.error(catchRequestError(error));
            emit('update:visible', false);
        } finally {
            loading.value = false;
        }
    },
    { immediate: true }
);

const reschedule = async () => {
    if (saving.value || loading.value) {
        return;
    }

    saving.value = true;

    try {
        const isNow = canScheduleNow.value && currentTab.value === 'now';
        const liveAt = isNow ? dayjs().tz(getClientTz()) : dayjs(date.value).tz(getClientTz());

        if (!isNow && campaignType.value && campaignTypeRestricted.value && !isDateTimeAllowed(liveAt.toDate())) {
            toasted.error('The selected date and time are unavailable. Please choose a permitted date and time.');
            return;
        }

        const liveAtUtc = toUTCDateTimeFormat(liveAt);

        await blastForecastClient.adjustCampaign({ teamId }, props.campaign.id, {
            live_at: liveAtUtc,
        });

        toasted.success('Blast re-scheduled');
        emit('rescheduled', liveAtUtc);
        emit('update:visible', false);
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        saving.value = false;
    }
};
</script>
